/**
 * 배달 매출 **가게별 보기**의 판정·계산 한 곳 (배달앱 가게 N:1 후속, 2026-11-04). 의존성 없는 순수 모듈이다 — 플랫폼 표
 * (`deliveryPlatform.js`)만 상대 import 로 읽는다.
 *
 * 한 매장(오더엔 store)에 같은 배달앱 가게(shop) 여럿이 붙으면 매출은 매장 하나로 모이는데, 관리 웹 화면이 가게를 가르지
 * 못했다. 서버가 가게 칸을 내리고(계약 정본 워크스페이스 `docs/planning/integrated-sales-shop-view-20261104.md` §1) 화면은
 * 이 모듈로 그 칸을 읽는다:
 *
 * - 가게 칸의 선택지(앱별 묶음·「매장 · 가게」 라벨) — [shopOptionGroups]·[chosenShop]
 * - 플랫폼을 옮길 때 가게가 남는가 — [shopNoForPlatform]
 * - 플랫폼별 정산 표의 펼친 가게 줄 — [platformTableRows]·[tableRowFilter]
 * - 대시보드 「주문 유형별 상세」의 배달 ▸ 앱 ▸ 가게 줄 — [deliveryOrderTypeRows]
 *
 * 「가게 구분 없음」은 가게 번호 없이 들어온 옛 매출(원본 POS·오터)이다 — 가게 줄의 합이 앱 줄과 맞게 한 줄로 모은다.
 */
import { DELIVERY_PLATFORM_FILTERS, deliveryAppGroupKey, platformDisplayName } from './deliveryPlatform.js'

/** 가게 번호 없는 몫의 이름 — 화면은 `$t` 로 그린다 */
export const NO_SHOP_LABEL = '가게 구분 없음'

/**
 * 매출 화면의 가게 이름 — 샵 매핑 이름, 없으면 가게 번호, 둘 다 없으면 `''`.
 *
 * 배달 메뉴 화면의 `deliveryMenuSync.shopText`(「이름(번호)」)와 규칙이 다르다 — 그 화면은 진단에 번호를 늘 보이고, 매출
 * 화면은 시안대로 이름만 보인다.
 */
export function salesShopName(shopName, shopNo) {
  if (shopName) return String(shopName)
  return shopNo != null && shopNo !== '' ? String(shopNo) : ''
}

/** 선택지가 여러 매장에 걸쳐 있는가 — 그러면 라벨에 매장 이름을 붙이고 글자로 찾는다 */
export function spansStores(shops) {
  return new Set((shops || []).map((shop) => shop.store_cd)).size > 1
}

/** 가게 칸의 값 — 앱과 가게 번호를 함께 쥔다(가게 번호만으로는 앱이 갈리지 않는다). 가게가 없으면 `''` */
export function shopKey(platform, shopNo) {
  return shopNo ? `${platform || ''}|${shopNo}` : ''
}

/** 묶음 차례 — 플랫폼 표의 필터 차례, 표에 없는 앱은 뒤 */
function platformRank(platform) {
  const index = DELIVERY_PLATFORM_FILTERS.indexOf(platform)
  return index < 0 ? DELIVERY_PLATFORM_FILTERS.length : index
}

/**
 * 가게 칸의 선택지 — 앱별 묶음(`el-option-group`).
 *
 * - 묶음 차례는 `DELIVERY_PLATFORM_FILTERS`, 묶음 안은 서버 차례(매장 이름 → 기준매출 큰 순) 그대로
 * - 라벨은 가게 이름([salesShopName]), 선택지가 여러 매장이면 「매장 이름 · 가게 이름」
 * - **고른 가게는 선택지에서 빠지지 않는다**: 기간을 바꿔 조회한 결과에 그 가게가 없거나(그 기간엔 N:1 이 아님) 거래 상세가
 *   쿼리로 이어받은 가게가 첫 조회 전이면, 그 가게를 번호로 한 줄 싣는다 — 안 그러면 보이지 않는 거르기가 남는다
 *
 * @param {Array<object>} shops 서버 `shops[]` — `{ store_cd, store_name, platform, shop_no, shop_name, … }`
 * @param {{platform?: string, shopNo?: string}} [chosen] 지금 고른 가게
 * @returns {Array<{platform: string, label: string, options: Array<{id, value, label, platform, shopNo}>}>}
 */
export function shopOptionGroups(shops, chosen) {
  const list = shops || []
  const manyStores = spansStores(list)
  const options = list.map((shop) => {
    const name = salesShopName(shop.shop_name, shop.shop_no)
    const value = shopKey(shop.platform, shop.shop_no)
    return {
      id: `${shop.store_cd}|${value}`,
      value,
      label: manyStores ? `${shop.store_name} · ${name}` : name,
      platform: shop.platform,
      shopNo: String(shop.shop_no),
    }
  })

  const chosenPlatform = (chosen && chosen.platform) || ''
  const chosenNo = (chosen && chosen.shopNo) || ''
  const chosenValue = shopKey(chosenPlatform, chosenNo)
  if (chosenValue && !options.some((option) => option.value === chosenValue)) {
    options.push({
      id: chosenValue,
      value: chosenValue,
      label: String(chosenNo),
      platform: chosenPlatform,
      shopNo: String(chosenNo),
    })
  }

  const groups = []
  options.forEach((option) => {
    let group = groups.find((candidate) => candidate.platform === option.platform)
    if (!group) {
      group = { platform: option.platform, label: platformDisplayName(option.platform), options: [] }
      groups.push(group)
    }
    group.options.push(option)
  })
  return groups.sort((a, b) => platformRank(a.platform) - platformRank(b.platform))
}

/**
 * **가게는 제 앱에서만 뜻이 있다** — 플랫폼을 `platform` 으로 옮길 때 남는 가게 번호. 옮겨 갈 앱이 고른 가게의 앱
 * (`current.platform` — 가게를 고르면 플랫폼이 그 앱으로 맞춰진다)과 같으면 그 가게, 다르면 `''`.
 *
 * 플랫폼 칸을 바꿀 때와 도넛을 눌러 거래 상세로 갈 때가 함께 쓴다 — 다른 앱에 그 가게 번호를 실으면 0건이 된다.
 *
 * @param {{platform?: string, shopNo?: string}} current 지금 조회 조건
 * @param {string} platform 옮겨 갈 플랫폼
 */
export function shopNoForPlatform(current, platform) {
  const shopNo = (current && current.shopNo) || ''
  return shopNo && (platform || '') === ((current && current.platform) || '') ? shopNo : ''
}

/** 가게 칸에서 고른 값 → `{ platform, shopNo }`. 비운 값·모르는 값은 `null` */
export function chosenShop(groups, value) {
  if (!value) return null
  for (const group of groups || []) {
    const option = group.options.find((candidate) => candidate.value === value)
    if (option) return { platform: option.platform, shopNo: option.shopNo }
  }
  return null
}

/**
 * 플랫폼별 정산 표의 줄 — `platforms[].shops` 가 있는 앱 줄에 가게 줄(`children`)을 단다(el-table tree, `row-key="rowKey"`).
 *
 * 앱 줄은 서버 줄 그대로에 `rowKey` 와, 가게 줄이 있으면 `children`·`shopCount`(가게 번호 있는 가게 수 — 「가게 N곳」)를
 * 더한다. 가게 줄은 서버 차례 그대로이고 `shop_no: null` 줄이 「가게 구분 없음」(`unknown`)이다. `shops` 가 없는 앱 줄은
 * 펼침이 없다 — 1:1 매장의 표는 지금과 같다.
 */
export function platformTableRows(platforms) {
  return (platforms || []).map((row) => {
    const shops = Array.isArray(row.shops) ? row.shops : []
    if (!shops.length) return { ...row, rowKey: row.platform }
    const children = shops.map((shop) => {
      const unknown = shop.shop_no == null || shop.shop_no === ''
      return {
        rowKey: `${row.platform}|${unknown ? '' : shop.shop_no}`,
        platform: row.platform,
        shopNo: unknown ? '' : String(shop.shop_no),
        name: unknown ? NO_SHOP_LABEL : salesShopName(shop.shop_name, shop.shop_no),
        unknown,
        expected_settlement: shop.expected_settlement,
        transaction_count: shop.transaction_count,
      }
    })
    return {
      ...row,
      rowKey: row.platform,
      shopCount: children.filter((child) => !child.unknown).length,
      children,
    }
  })
}

/**
 * 표의 줄을 누르면 거를 범위 — 앱 줄은 그 앱(가게 비움), 가게 줄은 그 가게와 앱, 「가게 구분 없음」은 누를 수 없다(`null`).
 */
export function tableRowFilter(row) {
  if (!row || row.unknown) return null
  return { platform: row.platform, shopNo: row.shopNo || '' }
}

/** 형제 줄과 같은 비율 셈 — 전체 대비, 소수 한 자리 문자열(전체가 0 이면 0) */
function sharePercent(value, total) {
  return total > 0 ? ((value / total) * 100).toFixed(1) : 0
}

/** 줄들을 열쇠별로 더한다 — 지역 누적만 한다 */
function sumBy(rows, keyOf, valueOf) {
  const sums = {}
  ;(rows || []).forEach((row) => {
    const key = keyOf(row)
    sums[key] = (sums[key] || 0) + valueOf(row)
  })
  return sums
}

const appKeyOf = (row) => deliveryAppGroupKey(row.type)

/**
 * 한 앱의 가게 줄 — 그 앱에 금액이 있는 가게가 2곳 이상일 때만, 아니면 `[]`.
 *
 * 금액이 0 인 가게(그 기간에 주문과 취소가 맞물린 가게)는 줄도 「곳」도 아니다. 가게 줄은 금액 큰 순이고, 앱 금액 − 가게 합이
 * 0 이 아니면 끝에 「가게 구분 없음」 한 줄을 단다(비교 값도 같은 셈).
 */
function shopRowsOf(group, app, shops, compareShops, total) {
  const inApp = (list) => (list || []).filter((row) => appKeyOf(row) === group)
  const current = inApp(shops)
  const amounts = sumBy(
    current,
    (row) => String(row.shopNo),
    (row) => Number(row.amount || 0)
  )
  const shopNos = Object.keys(amounts).filter((shopNo) => amounts[shopNo] !== 0)
  if (shopNos.length < 2) return []

  const names = {}
  current.forEach((row) => {
    const shopNo = String(row.shopNo)
    if (!names[shopNo] && row.shopName) names[shopNo] = row.shopName
  })
  const compareAmounts = sumBy(
    inApp(compareShops),
    (row) => String(row.shopNo),
    (row) => Number(row.amount || 0)
  )
  const sumOf = (sums) => Object.keys(sums).reduce((sum, key) => sum + sums[key], 0)

  const rows = shopNos
    .sort((a, b) => amounts[b] - amounts[a])
    .map((shopNo) => ({
      key: `delivery_${group}_shop_${shopNo}`,
      name: salesShopName(names[shopNo], shopNo),
      shop: true,
      value: amounts[shopNo],
      percent: sharePercent(amounts[shopNo], total),
      compareValue: compareAmounts[shopNo] || 0,
    }))

  const rest = app.value - sumOf(amounts)
  if (rest !== 0) {
    rows.push({
      key: `delivery_${group}_noshop`,
      name: NO_SHOP_LABEL,
      shop: true,
      unknown: true,
      value: rest,
      percent: sharePercent(rest, total),
      compareValue: app.compareValue - sumOf(compareAmounts),
    })
  }
  return rows
}

/**
 * 대시보드 「주문 유형별 상세」 배달 줄의 펼침 — 배달 ▸ 앱 ▸ 가게.
 *
 * 앱 줄은 매출 합계 `deliveryTps` 를 플랫폼 표의 그룹(`deliveryAppGroupKey`)으로 접은 것이다: 금액 큰 순, 색은 차례대로,
 * 비율은 전체 대비, 비교 값, 병기(참고) 지표 `settled_gross_amount`·`settled_count`(본 지표와 기준이 달라 합산하지 않는다).
 * 앱에 가게가 2곳 이상이면(`deliveryShops`, [shopRowsOf]) 그 앱 줄에 가게 줄(`children`)을 단다 — 가게 줄은 색 배지 없이
 * 글자로 그린다(`shop: true`). 앱마다 가게가 1곳 이하인 매장은 지금 화면과 같다.
 *
 * @param {{current: {deliveryTps?, deliveryShops?}, compare: ?{deliveryTps?, deliveryShops?}, total: number,
 *   colors: Array<string>}} input `current`·`compare` 는 매출 합계 응답(비교 기간이 없으면 `compare` 는 `null`)
 */
export function deliveryOrderTypeRows({ current, compare, total, colors }) {
  const tps = (current && current.deliveryTps) || []
  const amounts = sumBy(tps, appKeyOf, (row) => Number(row.amount || 0))
  const settledGross = sumBy(tps, appKeyOf, (row) => Number(row.settled_gross_amount || 0))
  const settledCount = sumBy(tps, appKeyOf, (row) => Number(row.settled_count || 0))
  const compareAmounts = sumBy(compare && compare.deliveryTps, appKeyOf, (row) => Number(row.amount || 0))

  return Object.keys(amounts)
    .filter((key) => amounts[key] > 0)
    .sort((a, b) => amounts[b] - amounts[a])
    .map((key, index) => {
      const row = {
        key: `delivery_${key}`,
        name: platformDisplayName(key),
        value: amounts[key],
        color: colors[index % colors.length],
        percent: sharePercent(amounts[key], total),
        compareValue: compareAmounts[key] || 0,
        settledGross: settledGross[key] || 0,
        settledCount: settledCount[key] || 0,
      }
      const shops = shopRowsOf(key, row, current && current.deliveryShops, compare && compare.deliveryShops, total)
      return shops.length ? { ...row, children: shops } : row
    })
}
